/**
 * Locale-preserving URL helper.
 *
 * When the operator is browsing on a localized URL (e.g. `/es/about/`) and
 * follows an internal link whose href doesn't include the locale prefix
 * (e.g. `/contact/`), naive navigation drops them back to the default
 * language. This helper rewrites same-origin URLs to keep the current
 * locale prefix in front, matching the `/{lang}/{path}` shape the server
 * already routes.
 *
 * No-op when:
 *   - href is cross-origin (don't touch external links)
 *   - href is malformed (can't parse — leave as-is)
 *   - the current locale IS the default locale (no prefix needed)
 *   - the URL already has a locale prefix (don't double-prefix)
 */
export function localizeUrl(href: string, defaultLocale: string): string {
  let url: URL;
  try {
    url = new URL(href, window.location.href);
  } catch {
    return href;
  }
  if (url.origin !== window.location.origin) return href;

  const currentMatch = window.location.pathname.match(/^\/([a-z]{2})(\/|$)/);
  const currentLocale = currentMatch ? currentMatch[1] : defaultLocale;
  if (currentLocale === defaultLocale) return url.href;

  // Already locale-prefixed — leave alone.
  if (/^\/[a-z]{2}(\/|$)/.test(url.pathname)) return url.href;

  url.pathname = '/' + currentLocale + url.pathname;
  return url.href;
}
